import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../../../hooks/useDebounce';
import { useFetch } from '../../../hooks/useFetch';
import { api } from '../../../services/api/apiClient';
import { cn } from '../../../utils/cn';
import { Button } from '../../ui/Button/Button';
import { CheckMark } from '../../ui/Checkbox/Checkbox';
import { Card, EmptyState, ErrorState, PageHeader, Skeleton } from '../../ui/Display/Display';
import { Icon } from '../../ui/Icon/Icon';
import { IconButton } from '../../ui/IconButton/IconButton';
import { MenuDropdown } from '../../ui/Menu/Menu';
import { useToast } from '../../ui/Toast/Toast';
import { ColumnsMenu, DateFilterMenu, FilterMenu } from '../FilterMenu/FilterMenu';
import type { Column, ListFilter, ListTab } from '../../../types/table';
import type { MenuItemSpec } from '../../ui/Menu/Menu';
import type { Paginated } from '../../../types/api';

/** What a row shows on phones. */
export interface MobileRow {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  badge?: ReactNode;
}

export interface ListPageProps<R = any> {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** API list endpoint, e.g. /customers */
  path: string;
  /** Always sent with the list request */
  baseQuery?: Record<string, any>;
  tabs?: ListTab[];
  tabKey?: string;
  filters?: ListFilter[];
  columns: Column<R>[];
  /** Card shown per row on phones */
  mobile: (row: R) => MobileRow;
  rowTo: (row: R) => string;
  rowActions?: (row: R, ctx: { reload: () => void }) => (MenuItemSpec | false | null | undefined | '' | 0)[];
  searchPlaceholder?: string;
  empty?: { icon?: string; title?: ReactNode; message?: ReactNode; action?: ReactNode };
  limit?: number;
  /** Summary cards above the table */
  stats?: ReactNode;
  exportName?: string;
  defaultSort?: string;
}

const HIDDEN_KEY = (title: string) => `electroz.columns.${title}`;
const readHidden = (title: string, columns: Column[]): string[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(HIDDEN_KEY(title)) ?? 'null');
    if (Array.isArray(saved)) return saved;
  } catch {
    /* storage blocked */
  }
  return columns.filter((c) => c.defaultHidden).map((c) => c.header);
};

/** 1 2 … 7 8 9 … 20 */
function pageList(page: number, pages: number): (number | string)[] {
  const set = new Set([1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages));
  const sorted = [...set].sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i && p - sorted[i - 1] > 1 ? ['…', p] : [p]));
}

function Pager({ meta, onPage }: { meta: Paginated<any>['meta']; onPage: (page: number) => void }) {
  if (!meta || meta.pages <= 1) return null;
  const btn = 'flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-sm tabular';
  return (
    <nav aria-label="Pages" className="flex items-center gap-1">
      <IconButton size="sm" variant="outline" icon="chevronLeft" label="Previous page" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)} className="h-8 w-8" />
      {pageList(meta.page, meta.pages).map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="px-1 text-ink-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            aria-current={p === meta.page ? 'page' : undefined}
            onClick={() => onPage(p as number)}
            className={cn(btn, p === meta.page ? 'border-navy-900 bg-navy-900 font-semibold text-white' : 'border-line bg-white text-navy-700 hover:bg-navy-50')}
          >
            {p}
          </button>
        ),
      )}
      <IconButton size="sm" variant="outline" icon="chevronRight" label="Next page" disabled={meta.page >= meta.pages} onClick={() => onPage(meta.page + 1)} className="h-8 w-8" />
    </nav>
  );
}

function downloadCsv(name: string, columns: Column[], rows: any[]) {
  const cols = columns.filter((c) => c.csv);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const text = [cols.map((c) => esc(c.header)).join(','), ...rows.map((r) => cols.map((c) => esc(c.csv?.(r))).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

/**
 * Smart table screen.
 *
 * tabs + tabKey: the document's main status, shown as tabs with live counts.
 * filters: dropdown buttons, each { key, label, icon, options } or { key, label, icon, fetcher, getLabel } (remote search),
 *          or { type: 'date', label } which writes from/to.
 * columns: [{ header, render(row), align, className, csv(row), defaultHidden, sort, sortDesc }]. csv() makes the column exportable;
 *          sort is the API field to order by (sortDesc: first click sorts newest / largest first).
 * defaultSort: the API's default order, e.g. '-date', shown as active on load.
 * rowActions(row, { reload }): items for the row's "…" menu.
 * stats(data): KPI cards above the table. mobile(row): { title, subtitle, right, badge } for phones.
 * Initial filters come from the URL, so links like /invoices?paymentStatus=unpaid work.
 */
export function ListPage<R extends { _id: string } = any>({
  title, subtitle, actions, path, baseQuery, tabs, tabKey = 'status', filters = [], columns, mobile, rowTo, rowActions,
  searchPlaceholder = 'Search', empty, limit = 20, stats, exportName, defaultSort = '',
}: ListPageProps<R>) {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const initial = Object.fromEntries(params);
  const [search, setSearch] = useState(initial.search ?? initial.q ?? '');
  const [query, setQuery] = useState<Record<string, any>>(() => {
    const { search: _s, q: _q, sort: _sort, ...rest } = initial;
    return { [tabKey]: tabs?.[0]?.value ?? '', ...rest };
  });
  const [labels, setLabels] = useState<Record<string, string>>({}); // chosen labels for remote filters
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(initial.sort || defaultSort);
  const [selected, setSelected] = useState<string[]>([]);
  const [hidden, setHidden] = useState(() => readHidden(title, columns));
  const [exporting, setExporting] = useState(false);
  const q = useDebounce(search);
  const queryKey = JSON.stringify(query);

  const setFilter = (patch: Record<string, any>) => {
    setQuery((s) => ({ ...s, ...patch }));
    setPage(1);
    setSelected([]);
  };
  const saveHidden = (h: string[]) => {
    setHidden(h);
    try {
      localStorage.setItem(HIDDEN_KEY(title), JSON.stringify(h));
    } catch {
      /* not remembered */
    }
  };

  // One request per change: the page of rows, plus the tab counts (?counts=<tabKey>) when there are tabs.
  const { data, loading, error, reload } = useFetch<Paginated<R>>(
    () => api.get(path, { ...baseQuery, ...query, search: q, sort, page, limit, ...(tabs ? { counts: tabKey } : {}) }),
    [path, queryKey, q, sort, page],
  );

  // Click: first direction -> opposite -> back to the default order.
  const toggleSort = (c: Column<R>) => {
    const key = c.sort ?? '';
    const first = c.sortDesc ? `-${key}` : key;
    const second = c.sortDesc ? key : `-${key}`;
    setSort((cur) => (cur === first ? second : cur === second && first !== defaultSort && second !== defaultSort ? defaultSort : first));
    setPage(1);
  };
  const sortState = (c: Column<R>): 'ascending' | 'descending' | 'none' => (sort === c.sort ? 'ascending' : sort === `-${c.sort}` ? 'descending' : 'none');
  const rows = data?.data ?? [];

  // Tab counts come with the list (same search and filters, any tab). A tab like "pending,assigned"
  // adds up its parts. The last counts stay on screen while the next page loads.
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  useEffect(() => {
    const raw = data?.meta?.counts;
    if (!tabs || !raw) return;
    setCounts(Object.fromEntries(tabs.map((t) => [t.value, raw[t.value] ?? t.value.split(',').reduce((a, v) => a + (raw[v] ?? 0), 0)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const visible = useMemo(() => columns.filter((c, i) => i === 0 || !hidden.includes(c.header)), [columns, hidden]);
  const anyFilter = !!q || filters.some((f) => (f.type === 'date' ? query.from || query.to : query[f.key]));
  const allChecked = rows.length > 0 && rows.every((r) => selected.includes(r._id));
  const canExport = columns.some((c) => c.csv);

  const exportAll = async () => {
    setExporting(true);
    try {
      const all: R[] = [];
      for (let p = 1; p <= 10; p += 1) {
        const res = await api.get<Paginated<R>>(path, { ...baseQuery, ...query, search: q, sort, page: p, limit: 200 });
        all.push(...res.data);
        if (p >= res.meta.pages) break;
      }
      downloadCsv(exportName ?? title.toLowerCase(), columns, all);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const from = data?.meta?.total ? (data.meta.page - 1) * data.meta.limit + 1 : 0;
  const to = data?.meta ? Math.min(data.meta.page * data.meta.limit, data.meta.total) : 0;

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle ?? (data?.meta ? `${data.meta.total} in total` : undefined)}
        actions={
          <>
            {canExport && <Button variant="outline" leftIcon="download" loading={exporting} onClick={exportAll}>Export</Button>}
            {actions}
          </>
        }
      />

      {stats && <div className="mb-5">{stats}</div>}

      <Card className="overflow-hidden">
        <div className="space-y-3 border-b border-line p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {tabs ? (
              <div role="tablist" aria-label={`${title} status`} className="scrollbar-none flex max-w-full gap-1 overflow-x-auto rounded-lg bg-navy-50 p-1">
                {tabs.map((t) => {
                  const active = (query[tabKey] ?? '') === t.value;
                  return (
                    <button
                      key={t.value || 'all'}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFilter({ [tabKey]: t.value })}
                      className={cn(
                        'flex h-8 shrink-0 items-center gap-2 rounded-md px-3 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-steel',
                        active ? 'bg-white font-semibold text-navy-900 shadow-soft' : 'text-ink-500 hover:text-navy-900',
                      )}
                    >
                      {t.label}
                      {counts[t.value] != null && (
                        <span className={cn('tabular rounded px-1.5 text-xs', active ? 'bg-brand-50 text-brand-700' : 'bg-navy-100 text-ink-500')}>{counts[t.value]}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span className="hidden lg:block" />
            )}
            <div className="relative lg:w-80">
              <Icon name="search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-9 w-full rounded-lg border border-line bg-white pl-9 pr-3 text-sm outline-none placeholder:text-ink-300 hover:border-navy-300 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {filters.map((f) =>
              f.type === 'date' ? (
                <DateFilterMenu key="date" label={f.label} value={{ from: query.from ?? '', to: query.to ?? '' }} onChange={(v) => setFilter(v)} />
              ) : (
                <FilterMenu
                  key={f.key}
                  label={f.label}
                  icon={f.icon}
                  options={f.options}
                  fetcher={f.fetcher}
                  getLabel={f.getLabel}
                  getDescription={f.getDescription}
                  value={query[f.key] ?? ''}
                  valueLabel={labels[f.key]}
                  onChange={(v, lab) => {
                    setLabels((l) => ({ ...l, [f.key]: lab ?? '' }));
                    setFilter({ [f.key]: v });
                  }}
                />
              ),
            )}
            {anyFilter && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSearch('');
                  setFilter({ ...Object.fromEntries(filters.map((f) => [f.key, ''])), from: '', to: '' });
                }}
              >
                Clear all
              </Button>
            )}
            <span className="flex-1" />
            {selected.length > 0 && (
              <>
                <span className="text-sm text-ink-500">{selected.length} selected</span>
                {canExport && (
                  <Button size="sm" variant="outline" leftIcon="download" onClick={() => downloadCsv(exportName ?? title.toLowerCase(), columns, rows.filter((r) => selected.includes(r._id)))}>
                    Export selected
                  </Button>
                )}
              </>
            )}
            <span className="hidden md:inline-flex">
              <ColumnsMenu columns={columns} hidden={hidden} onChange={saveHidden} />
            </span>
          </div>
        </div>

        {error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : loading && !data ? (
          <div className="space-y-3 p-4">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-11" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={empty?.icon}
            title={anyFilter || query[tabKey] ? 'Nothing matches these filters' : empty?.title ?? 'Nothing here yet'}
            message={anyFilter || query[tabKey] ? 'Try another search word or clear the filters.' : empty?.message}
            action={!anyFilter && !query[tabKey] && empty?.action}
          />
        ) : (
          <div className={cn(loading && 'opacity-60 transition-opacity')}>
            <ul className="divide-y divide-line md:hidden">
              {rows.map((r) => {
                const m = mobile(r);
                return (
                  <li key={r._id}>
                    <Link to={rowTo(r)} className="flex items-center gap-3 px-4 py-3 outline-none focus-visible:bg-navy-50">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-navy-900">{m.title}</p>
                        {m.subtitle && <p className="truncate text-[13px] text-ink-500">{m.subtitle}</p>}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        {m.right && <span className="tabular text-sm font-semibold text-navy-900">{m.right}</span>}
                        {m.badge}
                      </div>
                      <Icon name="chevronRight" size={16} className="shrink-0 text-ink-300" />
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-line bg-navy-50/40 text-[13px] text-ink-500">
                  <tr>
                    <th scope="col" className="w-12 py-3 pl-5 pr-0">
                      <button type="button" aria-label={allChecked ? 'Unselect all' : 'Select all on this page'} onClick={() => setSelected(allChecked ? [] : rows.map((r) => r._id))} className="flex rounded outline-none focus-visible:ring-2 focus-visible:ring-steel">
                        <CheckMark checked={allChecked} indeterminate={!allChecked && selected.length > 0} />
                      </button>
                    </th>
                    {visible.map((c) => {
                      const state = c.sort ? sortState(c) : null;
                      return (
                        <th key={c.header} scope="col" aria-sort={state ?? undefined} className={cn('whitespace-nowrap px-4 py-3 font-medium', c.align === 'right' && 'text-right', c.className)}>
                          {c.sort ? (
                            <button
                              type="button"
                              onClick={() => toggleSort(c)}
                              title={`Sort by ${c.header.toLowerCase()}`}
                              className={cn(
                                'group inline-flex items-center gap-1 rounded outline-none hover:text-navy-900 focus-visible:ring-2 focus-visible:ring-steel',
                                c.align === 'right' && 'flex-row-reverse',
                                state !== 'none' && 'font-semibold text-navy-900',
                              )}
                            >
                              {c.header}
                              <Icon
                                name={state === 'ascending' ? 'arrowUp' : state === 'descending' ? 'arrowDown' : 'sort'}
                                size={14}
                                strokeWidth={2}
                                className={state === 'none' ? 'text-ink-300 group-hover:text-ink-500' : 'text-brand-600'}
                              />
                            </button>
                          ) : (
                            c.header
                          )}
                        </th>
                      );
                    })}
                    {rowActions && <th scope="col" className="w-12 px-3"><span className="sr-only">Actions</span></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((r) => {
                    const checked = selected.includes(r._id);
                    return (
                      <tr key={r._id} onClick={() => navigate(rowTo(r))} className={cn('cursor-pointer', checked ? 'bg-brand-50/50' : 'hover:bg-navy-50/60')}>
                        <td className="py-3.5 pl-5 pr-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            aria-label={checked ? 'Unselect row' : 'Select row'}
                            onClick={() => setSelected((s) => (checked ? s.filter((x) => x !== r._id) : [...s, r._id]))}
                            className="flex rounded outline-none focus-visible:ring-2 focus-visible:ring-steel"
                          >
                            <CheckMark checked={checked} />
                          </button>
                        </td>
                        {visible.map((c, i) => (
                          <td key={c.header} className={cn('px-4 py-3.5', c.align === 'right' && 'tabular text-right', c.className)}>
                            {i === 0 ? (
                              // Plain text underlines on hover; rich cells (avatar + Cell) underline only the title.
                              <Link
                                to={rowTo(r)}
                                onClick={(e) => e.stopPropagation()}
                                className={cn('group rounded font-medium text-brand-600 outline-none focus-visible:ring-2 focus-visible:ring-steel', isText(c.render(r)) && 'hover:underline')}
                              >
                                {c.render(r)}
                              </Link>
                            ) : (
                              c.render(r)
                            )}
                          </td>
                        ))}
                        {rowActions && (
                          <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                            <MenuDropdown width={220} trigger={<IconButton icon="moreH" size="sm" label="Row actions" />} items={rowActions(r, { reload })} />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data && data.meta.total > 0 && (
          <div className="flex flex-col gap-3 border-t border-line px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="tabular text-sm text-ink-500">
              Showing {from}–{to} of {data.meta.total}
            </span>
            <Pager meta={data.meta} onPage={setPage} />
          </div>
        )}
      </Card>
    </>
  );
}

/** Two-line cell: bold primary text and a small grey second line. */
const isText = (node: ReactNode) => typeof node === 'string' || typeof node === 'number';

export function Cell({ title, sub, mono }: { title?: ReactNode; sub?: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="truncate font-medium text-navy-900 group-hover:text-brand-600 group-hover:underline">{title}</p>
      {sub && <p className={cn('truncate text-xs text-ink-400', mono && 'font-mono')}>{sub}</p>}
    </div>
  );
}

export default ListPage;
